import { SHERBERT_ASSETS_API_URL_V3 } from "./urls.js";
import { isInfoImageVariant, isInfoVariant, } from "./types/variants.js";
import { PathBuilder } from "./utils/PathBuilder.js";
import { QueryBuilder } from "./utils/QueryBuilder.js";
/**
 * Create a variant URL
 * @param options - The options for creating a variant URL
 * @param options.assetId - The ID of the asset
 * @param options.variants - The variants to get
 * @param options.signature - [optional] The signature to use
 * @param options.baseUrl - [optional] The base URL to use (default: SHERBERT_ASSETS_API_URL_V3)
 * @param options.version - [optional] The version of the asset
 * @param options.pageNum - [optional] The page number to fetch
 * @param options.itemNumber - [optional] The item number for extracted logos
 * @param options.showDeleted - [optional] Whether to show deleted variants
 * @returns The variant URL
 * @example
 * ```ts
 * const url = createVariantUrl({ assetId: asset.id, variants: [{ variant: "thumb" }], signature: signature, version: asset.version });
 * ```
 */
export const createVariantUrl = ({ assetId, variants, signature, baseUrl = SHERBERT_ASSETS_API_URL_V3, version, pageNum, itemNumber, showDeleted, }) => {
    const infoVariants = variants?.filter((v) => isInfoVariant(v.variant)) ?? [];
    const infoImageVariants = variants?.filter((v) => isInfoImageVariant(v.variant)) ?? [];
    if (infoVariants.length > 1) {
        throw new Error("Multiple info variants are not supported");
    }
    if (infoImageVariants.length > 1) {
        throw new Error("Multiple info image variants are not supported");
    }
    if (infoVariants.length > 0 && infoImageVariants.length > 0) {
        throw new Error("Cannot combine info and info image variants");
    }
    const path = PathBuilder()
        // Add the asset ID
        .add(assetId)
        // Add the version
        .add(version ? "versions" : undefined)
        .add(version ? version.toString() : undefined)
        // Add the page number
        .add(pageNum ? `page_${pageNum}` : undefined)
        // Add the variants
        .add(variants?.map((variant) => {
        if (variant.variant === "original") {
            return undefined;
        }
        let base = "";
        if (isInfoVariant(variant.variant)) {
            base = "info/";
        }
        else if (isInfoImageVariant(variant.variant)) {
            base = "info/image/";
        }
        base += variant.variant;
        const parametersAsString = Object.entries(variant.parameters ?? {})
            .filter(([, value]) => value !== undefined && value !== "")
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join(",");
        return `${base}${parametersAsString ? `:${parametersAsString}` : ""}`;
    }))
        .add(itemNumber ? `itemAt_${itemNumber}` : undefined)
        .toString();
    const query = QueryBuilder()
        .add("signature", signature)
        .add("showDeleted", showDeleted ? "true" : undefined)
        .toString();
    return `${baseUrl}${path}${query ? `?${query}` : ""}`;
};
